// Sprint 8 — composer attachment chip.
//
// Three states:
//   - 'pending'  → spinner + progress %, X removes (cancels in-flight upload).
//   - 'uploaded' → static icon (or thumbnail for image route) + name + size.
//   - 'failed'   → icon + name + reason; the X is replaced by a small retry.
//
// Sprint attachments-anthropic-parity Phase D (D7, D9) — when
// `route === 'image'`, the chip renders a real 64×64 thumbnail in place
// of the generic icon. Clicking the thumbnail opens an ImageLightbox
// (Esc / click-outside / swipe-down to close).
//
// Thumbnail source resolution (Cursor-mirror — show preview instantly):
//   - In the composer (pending or uploaded, `localFile` is set) →
//     `URL.createObjectURL(file)` so the founder sees the preview before
//     the upload finishes.
//   - On a persisted message bubble (`staticOnBubble`, no `localFile`) →
//     `GET /api/agents/:agentId/attachments/:chipId/raw` (Phase C endpoint).
//   - If the `<img>` fails to load (corrupt bytes, 404, or past the
//     30-day retention window — ATA10 / ATA15), `onError` falls back to
//     the generic icon. Send still works.
//
// Founder-mode rendering: humanized type label, KB/MB units, no MIME types.
// Mobile: ≥ 44 px tap target on the close button per founder-first §"Mobile-
// first contract".

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './atoms/Icon';
import type { ChipRoute, ChipState } from '../lib/attachments';
import { describeAttachment } from '../lib/attachments';
import { ImageLightbox } from './ImageLightbox';
import { getActiveBaseUrl } from '../stores/servers.store';
import { useTranslation } from '../i18n';

const ROUTE_ICON: Record<ChipRoute, IconName> = {
  image: 'Image',
  pdf: 'FileText',
  'text-inline': 'FileText',
  disk: 'Paperclip',
};

export interface AttachmentChipProps {
  id: string;
  route: ChipRoute;
  name: string;
  size: number;
  state?: ChipState;
  progress?: number; // 0..1
  errorMessage?: string;
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
  // When true, render in the "compact, on-bubble" variant (no buttons).
  // Used by UserMessage to render persisted chips after reload.
  staticOnBubble?: boolean;
  // Sprint attachments-anthropic-parity Phase D — agent the chip belongs
  // to. Required to build the `/api/agents/:agentId/attachments/:chipId/raw`
  // URL when this chip is in `route === 'image'` and `localFile` is not
  // available (post-send / bubble path). When omitted with an image route
  // and no `localFile`, the chip degrades to the generic icon (no 404
  // surfaced to the founder — ATA10).
  agentId?: string;
  // Sprint attachments-anthropic-parity Phase D — composer-side handle to
  // the in-memory `File` ref so the thumbnail renders BEFORE the upload
  // completes (Cursor-mirror: instant preview from a local blob URL).
  // Only forwarded by the composer; bubble chips never have it.
  localFile?: File;
}

export function AttachmentChip(props: AttachmentChipProps) {
  const {
    id,
    route,
    name,
    size,
    state = 'uploaded',
    progress = 1,
    errorMessage,
    onRemove,
    onRetry,
    staticOnBubble = false,
    agentId,
    localFile,
  } = props;

  const { t } = useTranslation();
  const label = describeAttachment({ name, size, route });

  // Local blob URL for in-composer images so the thumbnail appears the
  // instant the founder picks the file. Revoked on unmount or when the
  // file changes to avoid memory leaks.
  //
  // Guarded against environments without `URL.createObjectURL` (jsdom in
  // unit tests). In that case the chip falls back to the server-served
  // `/raw` URL — same code path as a persisted bubble chip.
  const localUrl = useMemo(() => {
    if (route !== 'image' || !localFile) return null;
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
    return URL.createObjectURL(localFile);
  }, [route, localFile]);

  useEffect(() => {
    if (!localUrl) return;
    return () => {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [localUrl]);

  // Pick the best available `src` for the <img>. Falls back to null
  // (renders the generic icon) when neither is available — which is the
  // graceful path for chips that have aged past the 30-day retention
  // window (ATA10).
  const thumbnailSrc = useMemo(() => {
    if (route !== 'image') return null;
    if (state === 'failed') return null;
    if (localUrl) return localUrl;
    if (agentId && state !== 'pending') {
      const base = getActiveBaseUrl();
      return `${base}/api/agents/${encodeURIComponent(agentId)}/attachments/${encodeURIComponent(id)}/raw`;
    }
    return null;
  }, [route, state, localUrl, agentId, id]);

  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  // Reset the failed flag whenever the src changes — a previously failed
  // image (e.g. local preview while the upload was racing) may be valid
  // when the server URL replaces it after `claim()`.
  useEffect(() => {
    setThumbnailFailed(false);
  }, [thumbnailSrc]);

  // Sprint work-segment-collapse (D013-EC-526) — the square frame is now
  // keyed on ROUTE + a resolvable src, never on load outcome. It used to be
  // `thumbnailSrc !== null && !thumbnailFailed`, so an `onError` swapped the
  // entire layout (64 px frame → ~26 px inline pill) and reflowed every
  // sibling section — measured 6/6 to be the actual cause of the sticky-
  // scroll-monotonicity spec's "flake" (a same-origin `/raw` 404 for an
  // aged-out attachment reflows identically, so this is a real production
  // path, not just a story fixture problem). The box is now invariant to
  // whether the image loads; only its CONTENTS (img vs. generic icon)
  // depend on `thumbnailFailed`.
  const showThumbnailFrame = route === 'image' && thumbnailSrc !== null;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Image-route chips with a resolvable src use a square frame layout (the
  // frame IS the chip, name/size below), whether or not the image itself
  // loads. All other routes use the inline-pill layout.
  if (showThumbnailFrame && thumbnailSrc) {
    return (
      <>
        <span
          className="group/chip inline-flex w-thumbnail-size flex-col items-stretch gap-1 min-w-0 rounded-md"
          data-testid={`attachment-chip-${id}`}
          data-state={state}
          data-route={route}
          title={label}
        >
          <span className="relative inline-block">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              disabled={thumbnailFailed}
              aria-label={
                thumbnailFailed ? t('composer.imageUnavailable') : t('composer.imageLightboxOpen')
              }
              title={
                thumbnailFailed ? `${name} — ${t('composer.imageUnavailable')}` : undefined
              }
              className="block h-thumbnail-size w-thumbnail-size overflow-hidden rounded-md border border-border bg-popover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`attachment-chip-${id}-thumbnail`}
            >
              {thumbnailFailed ? (
                <span
                  className="flex h-full w-full items-center justify-center"
                  aria-hidden="true"
                >
                  <Icon name={ROUTE_ICON.image} size={18} className="text-muted-foreground" />
                </span>
              ) : (
                <img
                  src={thumbnailSrc}
                  alt={name}
                  className="h-full w-full object-cover"
                  onError={() => setThumbnailFailed(true)}
                  draggable={false}
                />
              )}
            </button>
            {state === 'pending' && (
              <span
                className="absolute inset-0 flex items-center justify-center rounded-md bg-background/50"
                aria-hidden="true"
              >
                <Icon name="Loader2" size={18} className="animate-spin text-foreground" />
              </span>
            )}
            {!staticOnBubble && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(id)}
                aria-label={t('composer.removeAttachmentAriaLabel')}
                data-testid={`attachment-chip-${id}-remove`}
                className={[
                  // Floating X chip top-right corner of the thumbnail.
                  'absolute -right-1 -top-1 inline-flex items-center justify-center',
                  'h-5 w-5 rounded-full bg-popover border border-border',
                  'text-muted-foreground hover:text-foreground hover:bg-accent',
                  'shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                ].join(' ')}
              >
                <Icon name="X" size={12} aria-hidden="true" />
              </button>
            )}
          </span>
          <span
            className="text-caption text-muted-foreground truncate min-w-0 block"
            data-testid={`attachment-chip-${id}-name`}
          >
            {name}
            {state === 'pending' && progress < 1 && (
              <> · {Math.floor(progress * 100)}%</>
            )}
          </span>
        </span>
        <ImageLightbox
          open={lightboxOpen}
          src={thumbnailSrc}
          alt={name}
          onClose={() => setLightboxOpen(false)}
        />
      </>
    );
  }

  return (
    <span
      className={[
        'inline-flex items-center gap-2 rounded-md border bg-popover px-2 py-1',
        'text-caption text-foreground/90 max-w-full min-w-0',
        state === 'failed'
          ? 'border-status-error/40 bg-status-error/10 text-status-error'
          : 'border-border',
      ].join(' ')}
      title={label}
      data-testid={`attachment-chip-${id}`}
      data-state={state}
      data-route={route}
    >
      <span className="shrink-0">
        {state === 'pending' ? (
          <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
        ) : (
          <Icon name={ROUTE_ICON[route]} size={14} aria-hidden="true" />
        )}
      </span>
      <span className="truncate min-w-0">
        <span className="text-foreground">{name}</span>
        <span className="text-muted-foreground"> · {formatBytes(size)}</span>
        {state === 'pending' && progress < 1 && (
          <span className="text-muted-foreground"> · {Math.floor(progress * 100)}%</span>
        )}
        {state === 'failed' && errorMessage && (
          <span className="block text-status-error truncate" title={errorMessage}>
            {errorMessage}
          </span>
        )}
      </span>
      {!staticOnBubble && state === 'failed' && onRetry && (
        <ChipIconButton
          ariaLabel={t('composer.retryAttachmentAriaLabel')}
          onClick={() => onRetry(id)}
          icon={<Icon name="RotateCcw" size={14} aria-hidden="true" />}
          testId={`attachment-chip-${id}-retry`}
        />
      )}
      {!staticOnBubble && onRemove && (
        <ChipIconButton
          ariaLabel={t('composer.removeAttachmentAriaLabel')}
          onClick={() => onRemove(id)}
          icon={<Icon name="X" size={14} aria-hidden="true" />}
          testId={`attachment-chip-${id}-remove`}
        />
      )}
    </span>
  );
}

function ChipIconButton(props: {
  ariaLabel: string;
  onClick: () => void;
  icon: ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={props.ariaLabel}
      className={[
        // ≥ 44×44 px tap target on mobile per `project-context.md`
        // §Desktop-first (the ≥44 px target survives the 2026-08-11
        // correction; the section it used to cite does not); visually
        // compact via inset.
        'shrink-0 inline-flex items-center justify-center',
        'h-11 w-11 sm:h-6 sm:w-6 -m-2 sm:m-0 p-0 rounded-md',
        'text-muted-foreground hover:text-foreground',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
      ].join(' ')}
      data-testid={props.testId}
    >
      {props.icon}
    </button>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
